import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { connect, useDispatch } from "react-redux";
import {
  Form,
  Card,
  Button,
  Tooltip,
  Space,
  Divider,
  Alert,
  message,
  List,
  Typography,
  Spin,
  Collapse,
  Input,
  Checkbox,
  DatePicker,
} from "antd";
import {
  FormOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  DownOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import {
  mapGroupToInitialValues,
  displayNameAsyncValidator,
} from "./form/ValidationUtils";

import {
  searchGroupIdentifier,
  getMembershipRestriction,
} from "../../actions/api";
import { showErrorNotification, formatDateGroup } from "../../util";
import { enableGidAssignment, FormValidationStates } from "../../config";

import {
  PrivacyRadioGroup,
  privacyTypes,
  resourceCategories,
  ResourceCategoryRadioGroup,
  SelfSubscriptionRadioGroup,
  selfSubscriptionTypes,
} from "../../constants";

import { DynamicGroupTypes, GroupSyncTypes } from "../../constants/enums";

import OwnerEdit from "../common/OwnerEdit";
import TooltipIcon from "../common/TooltipIcon";
import AssignAdministratorGroup from "./formHelpers/AssignAdministratorGroup";
import GroupDeletionAction from "./GroupDeletionAction";
import ComputingGroupAction from "./ComputingGroupAction";
import GroupMembershipModal from "./GroupMembershipModal";
import "./GroupEditForm.css";
import { UserCategory } from "./GroupUtils";
import GroupAdvancedSettings from "./editForm/GroupAdvancedSettings";
import BlockGroupButton from "./BlockGroupButton";
import CopyGroupButton from "./CopyGroupButton";

/* Layout reused */
const formItemLayout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
  style: { marginBottom: 4 },
};

const PlaceholderLoading = ({ loading, children, small }) => {
  if (loading) {
    return (
      <Spin
        spinning={true}
        size={small ? "small" : "large"}
        aria-label="Loading"
      >
        <div style={{ minHeight: small ? 40 : 120 }} />
      </Spin>
    );
  }
  return children;
};

const GroupEditFormV2 = ({
  handleSubmit,
  dynamic,
  onSelectOwner,
  group = {},
  searchGroupIdentifier,
  canManage,
  groupMemberships,
  loading,
  isDisabledByInternalState,
  isReader,
  userCategory,
  managedButMembershipModifiable,
  isAdmin,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [displayNameStatus, setDisplayNameStatus] = useState(null);
  const [displayNameError, setDisplayNameError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [membershipRestrictionGroups, setMembershipRestrictionGroups] =
    useState([]);
  const [activePanels, setActivePanels] = useState([
    "generalSettings",
    "securityAndLifecycleSettings",
    "membershipSettings",
  ]);

  const selfSubscriptionWatch = Form.useWatch("selfSubscriptionType", form);
  const initialValues = mapGroupToInitialValues(group);
  initialValues.resourceCategory =
    initialValues.resourceCategory ?? resourceCategories.Test;
  initialValues.selfSubscriptionType =
    initialValues.selfSubscriptionType ?? selfSubscriptionTypes.Closed;
  initialValues.privacyType = initialValues.privacyType ?? privacyTypes.Admins;
  initialValues.enforceMembershipRestrictions =
    typeof initialValues.enforceMembershipRestrictions === "undefined"
      ? !!group.enforceMembershipRestrictions
      : initialValues.enforceMembershipRestrictions;

  const disabledBySyncType = () =>
    group.syncType !== GroupSyncTypes.Master &&
    group.syncType !== GroupSyncTypes.NoSync;

  const handleCopySubscriptionLink = () => {
    const currentUrl = window.location.href;
    const identifier = group.groupIdentifier;
    const baseGroupUrl = currentUrl.replace(/\/details$/, "");
    const subscriptionLink = baseGroupUrl.replace(
      /\/group\/[^/]+$/,
      `/group/${identifier}/subscribe`
    );

    navigator.clipboard
      .writeText(subscriptionLink)
      .then(() => message.success("Subscription link copied"))
      .catch(() => message.error("Failed to copy link"));
  };

  const loadMembershipRestrictions = useCallback(async () => {
    setInitialLoading(true);
    if (!canManage && !isReader) {
      setInitialLoading(false);
      return;
    }
    try {
      const memberships = await dispatch(getMembershipRestriction(group.id));
      if (Array.isArray(memberships?.payload?.data)) {
        setMembershipRestrictionGroups(memberships.payload.data);
      } else if (memberships?.payload?.data?.message) {
        showErrorNotification(
          memberships.payload.title || "Error",
          memberships.payload.data.message
        );
      } else {
        setMembershipRestrictionGroups([]);
      }
    } catch (err) {
      showErrorNotification(
        "Error loading groups details",
        err?.message || err
      );
    } finally {
      setInitialLoading(false);
    }
  }, [dispatch, canManage, isReader, group.id]);

  useEffect(() => {
    if (group && group.id) loadMembershipRestrictions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, loadMembershipRestrictions]);

  const displayNameValidator = (originalName) => async (_, value) => {
    const trimmed = (value || "").trim();
    if (!trimmed || /^\s/.test(value)) {
      setDisplayNameStatus(FormValidationStates.Error);
      setDisplayNameError(
        "The Group display name cannot be empty or start with a space."
      );
      return Promise.reject(
        new Error(
          "The Group display name cannot be empty or start with a space."
        )
      );
    }
    if (trimmed === originalName) {
      setDisplayNameStatus(FormValidationStates.Success);
      return Promise.resolve();
    }
    setDisplayNameStatus(FormValidationStates.Validating);
    setDisplayNameError(null);
    try {
      await displayNameAsyncValidator(searchGroupIdentifier)(_, value);
      setDisplayNameStatus(FormValidationStates.Success);
      setDisplayNameError(null);
      return Promise.resolve();
    } catch (err) {
      setDisplayNameStatus(FormValidationStates.Error);
      setDisplayNameError(err?.message || "Display name validation failed");
      return Promise.reject(err);
    }
  };

  const onFinish = async (values) => {
    let processedValues = { ...values, dynamic };
    if (dynamic) {
      processedValues.dynamicGroupType = values.checkboxDynamicGroupType
        ? DynamicGroupTypes.FullDynamic
        : DynamicGroupTypes.Dynamic;
      processedValues.removeNonActiveMembers = !values.checkboxDynamicGroupType;
    }
    processedValues.validityLimit = formatDateGroup(values.validityLimit);
    await handleSubmit(processedValues);
  };

  const renderExpirationDateWarning = () => {
    const touched = form.isFieldsTouched(["validityLimit"], true);
    const val = form.getFieldValue("validityLimit");
    if (!val && touched) {
      return (
        <Alert
          type="warning"
          role="region"
          aria-label="Expiration date warning"
          showIcon
          icon={
            <ExclamationCircleOutlined
              style={{ marginRight: 16 }}
              aria-hidden="true"
            />
          }
          message={
            <>
              Before removing the expiration date, please consider whether
              maintaining this indefinitely conforms with{" "}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="http://cds.cern.ch/record/2651311"
              >
                Operational Circular No.11
              </a>{" "}
              "The processing of personal data at CERN"
            </>
          }
          style={{ marginBottom: 16 }}
        />
      );
    }
    return null;
  };
  const canShowCopyButton = isAdmin || userCategory === UserCategory.OWNER;

  const createButtons = () => (
    <Form.Item style={{ marginTop: 24 }}>
      <Button
        aria-label="Save"
        id="updateGroupButton"
        type="primary"
        htmlType="submit"
        loading={loading}
        disabled={
          disabledBySyncType() || isDisabledByInternalState || !canManage
        }
      >
        Save
      </Button>
      {group && group.id && (
        <>
          {!disabledBySyncType() &&
            !isDisabledByInternalState &&
            canManage &&
            !managedButMembershipModifiable && (
              <>
                {canShowCopyButton && (
                  <>
                    <Divider type="vertical" />
                    <CopyGroupButton canManage={canManage} />
                  </>
                )}
                <Divider type="vertical" />
                <GroupDeletionAction groupId={group.id} />
                <Divider type="vertical" />
                <BlockGroupButton canManage={canManage} />
              </>
            )}
        </>
      )}
    </Form.Item>
  );

  useEffect(() => {
    form.setFieldsValue(mapGroupToInitialValues(group));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, form]);

  const handlePanelChange = (keys) => {
    setActivePanels(keys);
  };

  const getCollapseItems = () =>
    [
      {
        key: "generalSettings",
        label: <strong>General</strong>,
        children: (
          <>
            <Form.Item
              {...formItemLayout}
              name="groupIdentifier"
              label="Group identifier"
            >
              <Input
                id="groupIdentifierInput"
                disabled
                prefix={<UserOutlined />}
                placeholder="my-group"
              />
            </Form.Item>
            <Form.Item
              {...formItemLayout}
              name="displayName"
              label="Group display name"
              hasFeedback
              validateStatus={displayNameStatus}
              help={displayNameError}
              rules={[
                { required: true, message: "Please input the group's name!" },
                { validator: displayNameValidator(group.displayName) },
              ]}
            >
              <Input
                id="groupDisplayNameInput"
                disabled={
                  (disabledBySyncType() && !displayNameError) ||
                  !canManage ||
                  isDisabledByInternalState ||
                  managedButMembershipModifiable
                }
                placeholder="My group"
                onBlur={() =>
                  form.validateFields(["displayName"]).catch(() => {})
                }
              />
            </Form.Item>
            <Form.Item
              {...formItemLayout}
              name="description"
              label="Description"
              rules={[
                {
                  required: true,
                  message: "Please input the group's description!",
                },
                {
                  pattern: /\S+/,
                  message: "Description cannot be only spaces!",
                },
              ]}
            >
              <Input.TextArea
                id="groupDescriptionInput"
                disabled={
                  disabledBySyncType() ||
                  !canManage ||
                  isDisabledByInternalState ||
                  managedButMembershipModifiable
                }
                placeholder="Group description"
                rows={3}
              />
            </Form.Item>
            <Form.Item
              {...formItemLayout}
              label="Administrator group"
              name="administratorsId"
            >
              <AssignAdministratorGroup
                disabled={
                  disabledBySyncType() ||
                  !canManage ||
                  isDisabledByInternalState ||
                  managedButMembershipModifiable
                }
                onChange={() => {}}
              />
            </Form.Item>
            <Form.Item {...formItemLayout} label="Owner" name="owner">
              {group && (
                <OwnerEdit
                  onSelect={onSelectOwner}
                  loading={false}
                  group={group}
                  disabled={
                    disabledBySyncType() ||
                    !canManage ||
                    (userCategory !== UserCategory.GLOBAL_ADMIN &&
                      userCategory !== UserCategory.OWNER &&
                      userCategory !== UserCategory.ADMIN)
                  }
                />
              )}
            </Form.Item>
          </>
        ),
      },
      {
        key: "securityAndLifecycleSettings",
        label: <strong>Security and lifecycle</strong>,
        children: (
          <>
            <Form.Item
              {...formItemLayout}
              name="validityLimit"
              label="Expiration date"
            >
              <DatePicker
                disabled={
                  disabledBySyncType() ||
                  isDisabledByInternalState ||
                  !canManage ||
                  managedButMembershipModifiable
                }
                disabledDate={(c) => c && c.isBefore(dayjs(), "day")}
                placeholder="dd-mm-yyyy"
              />
            </Form.Item>
            {(canManage || isReader) && (
              <>
                <Form.Item
                  {...formItemLayout}
                  name="blockingDeadline"
                  label="Blocking date"
                >
                  <DatePicker disabled format="DD-MM-YYYY" />
                </Form.Item>
              </>
            )}
            <Form.Item
              {...formItemLayout}
              label={
                <span>
                  Resource category
                  <TooltipIcon title="Test - will be removed in a short time; Official - will be reassigned to your supervisor in case of departure; Personal - will be deleted in case of departure." />
                </span>
              }
              name="resourceCategory"
            >
              <ResourceCategoryRadioGroup
                disabled={
                  disabledBySyncType() ||
                  isDisabledByInternalState ||
                  !canManage ||
                  managedButMembershipModifiable
                }
              />
            </Form.Item>
          </>
        ),
      },
      (canManage || isReader) && {
        key: "membershipSettings",
        label: <strong>Membership settings</strong>,
        children: (
          <>
            {(canManage || isReader) && (
              <>
                {!dynamic && (
                  <>
                    <Form.Item
                      {...formItemLayout}
                      label={
                        <Space>
                          Self-subscription policy
                          {selfSubscriptionWatch !==
                            selfSubscriptionTypes.Closed && (
                            <Tooltip title="Copy subscription link">
                              <Button
                                aria-label="Copy subscription link"
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={handleCopySubscriptionLink}
                              />
                            </Tooltip>
                          )}
                        </Space>
                      }
                      name="selfSubscriptionType"
                    >
                      <SelfSubscriptionRadioGroup
                        disabled={
                          disabledBySyncType() ||
                          isDisabledByInternalState ||
                          !canManage
                        }
                      />
                    </Form.Item>
                    {selfSubscriptionWatch !== selfSubscriptionTypes.Closed && (
                      <Form.Item
                        {...formItemLayout}
                        name="approvalRequired"
                        valuePropName="checked"
                        label="Approval required"
                      >
                        <Checkbox
                          disabled={
                            disabledBySyncType() ||
                            isDisabledByInternalState ||
                            !canManage
                          }
                        />
                      </Form.Item>
                    )}
                    <Form.Item
                      {...formItemLayout}
                      name="removeNonActiveMembers"
                      valuePropName="checked"
                      label={
                        <span>
                          Purge non active users?
                          <TooltipIcon title="When identities become inactive, e.g. they leave CERN, they will be removed from this group if this checkbox is ticked." />
                        </span>
                      }
                    >
                      <Checkbox
                        disabled={
                          disabledBySyncType() ||
                          isDisabledByInternalState ||
                          !canManage
                        }
                      />
                    </Form.Item>
                  </>
                )}

                <Form.Item
                  {...formItemLayout}
                  label="Membership and Mail Properties visibility"
                  name="privacyType"
                  shouldUpdate={(prev, cur) =>
                    prev.privacyType !== cur.privacyType
                  }
                >
                  <span>
                    <PrivacyRadioGroup
                      disabled={
                        disabledBySyncType() ||
                        isDisabledByInternalState ||
                        !canManage
                      }
                      defaultValue={initialValues.privacyType}
                    />
                    <br />
                    <i style={{ color: "red" }}>
                      The group memberships and mail properties will be shared
                      with other applications and external clouds that may not
                      provide the same level of confidentiality.
                    </i>
                  </span>
                </Form.Item>
                {enableGidAssignment && (
                  <Form.Item
                    {...formItemLayout}
                    label={
                      <span>
                        Set as Computing Group?
                        <TooltipIcon title="Allow a group to have a gid assigned to it and become a computing group. Note that if a group becomes a computing group then this cannot be reversed!" />
                      </span>
                    }
                    name="setComputingGroup"
                    valuePropName="checked"
                  >
                    <ComputingGroupAction
                      groupComputingGroup={group.isComputingGroup}
                      groupId={group.id}
                    />
                  </Form.Item>
                )}
                {dynamic && (
                  <Form.Item
                    {...formItemLayout}
                    name="checkboxDynamicGroupType"
                    valuePropName="checked"
                    label={
                      <span>
                        Include ex-users and ex-employees?
                        <TooltipIcon title="Include all people who were registered in the CERN people database at any time to the results of the dynamic group criteria. When disabled (default option) only people with an active CERN registration will appear in the group." />
                      </span>
                    }
                  >
                    <Checkbox disabled={isDisabledByInternalState} />
                  </Form.Item>
                )}
              </>
            )}
            <Spin
              spinning={initialLoading}
              aria-label="Loading membership restrictions"
            >
              <Form.Item
                {...formItemLayout}
                label={
                  <span>
                    Membership Restrictions
                    <TooltipIcon title="If specified, non-admin users will only be able to add members that are already members of one of the restriction groups. No restrictions are applied if the list is empty." />
                  </span>
                }
              >
                <div
                  style={{
                    display: "flex",
                    position: "relative",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  }}
                >
                  <GroupMembershipModal
                    groupId={group.id}
                    disabled={
                      disabledBySyncType() ||
                      isDisabledByInternalState ||
                      !canManage
                    }
                    onCancel={loadMembershipRestrictions}
                  />
                </div>
                <Alert
                  type="warning"
                  role="region"
                  aria-label="Membership restrictions information"
                  style={{
                    width: "80%",
                    maxHeight: "150px",
                    overflowY: "auto",
                    marginTop: 8,
                    ...(membershipRestrictionGroups?.length > 0
                      ? { padding: "0 12px" }
                      : {}),
                  }}
                  message={null}
                  description={
                    <>
                      {membershipRestrictionGroups?.length > 0 ? (
                        <List
                          size="small"
                          dataSource={membershipRestrictionGroups}
                          renderItem={(g) => (
                            <List.Item>{g.displayName}</List.Item>
                          )}
                        />
                      ) : (
                        <Typography.Text type="secondary" italic>
                          No membership restrictions configured.
                        </Typography.Text>
                      )}
                    </>
                  }
                />
              </Form.Item>
              <Form.Item
                {...formItemLayout}
                name="enforceMembershipRestrictions"
                valuePropName="checked"
                label={
                  <span>
                    Enforce membership restrictions
                    <TooltipIcon title="If the option to enforce membership restrictions is selected, members that are not members of at least one of the following groups will be periodically removed. Restrictions for new group members will also be enforced for admin users. No restrictions are applied if the list is empty." />
                  </span>
                }
              >
                <Checkbox
                  disabled={
                    disabledBySyncType() ||
                    isDisabledByInternalState ||
                    !canManage
                  }
                />
              </Form.Item>
            </Spin>
          </>
        ),
      },
      {
        key: "advancedSettings",
        label: (
          <>
            <strong>Advanced settings</strong>{" "}
            <TooltipIcon
              title="These advanced settings are provided for compatibility with legacy systems. They are not recommended for standard use."
              color="red"
            />
          </>
        ),
        children: (
          <GroupAdvancedSettings
            canManage={canManage}
            formItemLayout={formItemLayout}
          />
        ),
      },
    ].filter(Boolean);

  return (
    <Card
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          <FormOutlined
            style={{ marginRight: 8, color: "#bfbfbf" }}
            aria-hidden="true"
          />
          Manage Group Details
        </Typography.Title>
      }
      style={{ marginBottom: 24 }}
    >
      <PlaceholderLoading loading={loading}>
        {renderExpirationDateWarning()}
        <Form
          form={form}
          layout="horizontal"
          initialValues={initialValues}
          onFinish={onFinish}
        >
          <Collapse
            activeKey={activePanels}
            onChange={handlePanelChange}
            expandIcon={({ isActive }) => (
              <DownOutlined rotate={isActive ? 180 : 0} aria-hidden="true" />
            )}
            bordered={false}
            className="group-edit-collapse"
            items={getCollapseItems()}
          />
          {createButtons()}
        </Form>
      </PlaceholderLoading>
    </Card>
  );
};

GroupEditFormV2.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  dynamic: PropTypes.bool.isRequired,
  onSelectOwner: PropTypes.func,
  group: PropTypes.object,
  searchGroupIdentifier: PropTypes.func.isRequired,
  canManage: PropTypes.bool,
  groupMemberships: PropTypes.array,
  loading: PropTypes.bool,
  isDisabledByInternalState: PropTypes.bool,
  isReader: PropTypes.bool,
  userCategory: PropTypes.string,
  managedButMembershipModifiable: PropTypes.bool,
};

export default connect(null, { searchGroupIdentifier })(GroupEditFormV2);
